import {
  buildDataTableCols,
  createCheckboxPreset,
  type DataTableCol,
  type DataTableColumn
} from '@/components/custom/erp/data-table-v2';
import { buildInputTableCols, type InputTableCol } from '@/components/custom/erp/input-table-v2';
import type { SelectSearchOption } from '@/components/custom/erp/data-table-v2/search-inputs';
import { ConversionQuantityFieldV2, DvtFieldV2 } from '@/components/custom/erp/dvt-field-v2';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import { Checkbox } from '@/components/ui/checkbox';

type HoaDonBanHangRow = BanHangRecord;

const STATUS_OPTIONS: SelectSearchOption[] = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Xuất hóa đơn' },
  { value: '6', label: 'Đã xuất hóa đơn điện tử' },
  { value: '7', label: 'Bỏ duyệt đơn hàng' },
  { value: '9', label: 'Hủy' }
];

const HDDT_STATUS_OPTIONS: SelectSearchOption[] = [
  { value: '0', label: 'Không sử dụng' },
  { value: '1', label: 'Chờ phát hành' },
  { value: '3', label: 'Khác' }
];

export const BASE_COLS: DataTableCol<HoaDonBanHangRow>[] = [
  {
    field: 'hdbh_yn',
    headerName: 'Hoá đơn',
    align: 'center',
    preset: createCheckboxPreset({
      getCheckedValue: row => Boolean(row?.hdbh_yn),
      renderCheckbox: checked => <Checkbox checked={checked} />
    })
  },
  {
    field: 'px_yn',
    headerName: 'Xuất kho',
    align: 'center',
    width: 120,
    preset: createCheckboxPreset({
      getCheckedValue: row => Boolean(row?.px_yn),
      renderCheckbox: checked => <Checkbox checked={checked} />
    })
  },
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    valueGetter: row => row?.status,
    valueFormatter: value => STATUS_OPTIONS.find(option => option.value === String(value ?? ''))?.label ?? '',
    filter: { type: 'multiple', options: STATUS_OPTIONS }
  },
  {
    field: 'ma_tthddt',
    headerName: 'Trạng thái HĐĐT',
    width: 150,
    valueGetter: row => row?.ma_tthddt,
    valueFormatter: value => HDDT_STATUS_OPTIONS.find(option => option.value === String(value ?? ''))?.label ?? '',
    filter: { type: 'multiple', options: HDDT_STATUS_OPTIONS }
  },
  { field: 'so_ct', headerName: 'Số c/từ', width: 200, valueGetter: row => row?.so_ct },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    valueGetter: row => row?.ngay_ct,
    valueFormatter: value => formatDate(value as BanHangRecord),
    filter: { type: 'date' }
  },
  {
    field: 'so_ct_hddt0',
    headerName: 'Số hóa đơn',
    width: 120,
    valueGetter: row => row?.so_ct_hddt0 || row?.so_ct_hddt || ''
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 120,
    valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || ''
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    valueGetter: row => row?.ma_kh_data?.customer_name || row?.ten_kh || ''
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200,
    valueGetter: row => row?.dien_giai
  },
  {
    field: 'tk',
    headerName: 'Tk nợ',
    width: 100,
    valueGetter: row => row?.tk_data?.code || row?.tk || ''
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng tiền',
    width: 150,
    columnType: 'number',
    align: 'right',
    valueGetter: row => row?.t_tt_nt,
    valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
    filter: { type: 'text' }
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || ''
  }
];
export const DATA_TABLE_COLUMNS = (
  handleViewClick: (row?: HoaDonBanHangRow) => void,
  amount?: number | null,
  isLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isLoading ? 'Đang tính...' : typeof amount === 'number' ? formatRoundedNumber(amount) : '—'}
      </span>
    </div>
  );

  return buildDataTableCols(
    BASE_COLS.map(col => {
      if (col.field === 'so_ct') {
        return {
          ...col,
          renderCell: params => (
            <button
              onClick={event => {
                event.stopPropagation();
                handleViewClick(params.row);
              }}
              className='hover:text-blue-500 hover:underline'
            >
              {params.row?.so_ct}
            </button>
          )
        };
      }

      if (col.field === 't_tt_nt') {
        return { ...col, filter: { type: 'text' as const, after: total } };
      }

      return col;
    })
  );
};

export const INPUT_TABLE_BASE_COLS: InputTableCol<HoaDonBanHangRow>[] = [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 130,
    renderCell: params => <DvtFieldV2 row={params.row} />
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 120,
    renderCell: params => params.row.ma_kho_data?.ma_kho
  },
  {
    field: 'sl_ton',
    headerName: 'Tồn',
    width: 100,
    align: 'right'
  },
  {
    field: 'ct_km',
    headerName: 'Loại hàng',
    width: 120,
    renderCell: params => {
      switch (params.row.ct_km) {
        case false:
          return 'Hàng bán';
        case true:
          return 'Hàng KM';
        default:
          return '';
      }
    }
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    renderCell: params => <ConversionQuantityFieldV2 row={params.row} />
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá bán VND',
    align: 'right',
    width: 150,
    renderCell: params => formatMoney(params.row.gia_nt2)
  },
  {
    field: 'tien_nt2',
    headerName: 'Thành tiền VND',
    align: 'right',
    width: 150,
    renderCell: params => formatMoney(params.row.tien_nt2)
  },
  {
    field: 'tl_ck',
    align: 'right',
    headerName: 'Tl ck(%)',
    width: 150
  },
  {
    field: 'px_dd',
    headerName: 'Đích danh',
    width: 120,
    columnType: 'boolean',
    align: 'center',
    renderCell: params => <Checkbox checked={params.row.px_dd} />
  },
  {
    field: 'gia_nt',
    align: 'right',
    headerName: 'Giá tồn VND',
    width: 150,
    renderCell: params => formatMoney(params.row.gia_nt)
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    align: 'right',
    width: 150,
    renderCell: params => formatMoney(params.row.tien_nt)
  },
  {
    field: 'ma_thue',
    align: 'right',
    headerName: 'Thuế suất',
    width: 150,
    renderCell: params => params.row.ma_thue_data?.ten_thue
  },
  {
    field: 'tk_thue_co',
    headerName: 'Tk thuế có',
    width: 120,
    renderCell: params => params.row.tk_thue_co_data?.code
  },
  {
    field: 'tk_dt',
    headerName: 'Tk doanh thu',
    width: 120,
    renderCell: params => params.row.tk_dt_data?.code
  },
  {
    field: 'tk_gv',
    headerName: 'Tk giá vốn',
    width: 120,
    renderCell: params => params.row.tk_gv_data?.code
  },
  {
    field: 'tk_vt',
    headerName: 'Tk kho',
    width: 120,
    renderCell: params => params.row.tk_vt_data?.code
  },
  {
    field: 'tk_ck',
    headerName: 'Tk chiết khấu',
    width: 120,
    renderCell: params => params.row.tk_ck_data?.code
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => params.row.ma_vv_data?.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: params => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: params => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: params => params.row.ma_cp0_data?.ma_cpkhl
  }
];
export const INPUT_TABLE_COLUMNS = buildInputTableCols(INPUT_TABLE_BASE_COLS);

export const historyColumns: DataTableColumn[] = buildDataTableCols([
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
]);
